// UserMenu replaces upstream's profile popover in the app bar: who is signed
// in, their profile, the admin panel, appearance and sign out.
import React, { useContext, useState } from 'react'
import { useDispatch } from 'react-redux'
import { AnyAction, Dispatch } from 'redux'
import { Box, ButtonBase, IconButton, Popover, Typography } from '@mui/material'
import {
  Build,
  ChevronRight,
  DarkMode,
  LightMode,
  Logout,
  Person,
  SettingsBrightness,
} from '@mui/icons-material'
import { authLogout } from '../actions'
import AppLink from '../util/AppLink'
import { CurrentUserAvatar } from '../util/avatars'
import { useSessionInfo } from '../util/RequireConfig'
import { ThemeContext } from '../theme/themeConfig'
import { placeCity, usePlaces, useTimezones, zoneName } from './tz'
import { useBrand } from './ui'

const modes = [
  { value: 'light', label: 'Light', icon: <LightMode /> },
  { value: 'system', label: 'System', icon: <SettingsBrightness /> },
  { value: 'dark', label: 'Dark', icon: <DarkMode /> },
] as const

function MenuLink(props: {
  to: string
  icon: React.ReactNode
  label: string
  onClick: () => void
}): React.JSX.Element {
  const c = useBrand()
  return (
    <AppLink
      to={props.to}
      underline='none'
      onClick={props.onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.25,
        height: 40,
        borderRadius: '10px',
        color: c.ink,
        fontSize: 14,
        fontWeight: 500,
        '& svg': { fontSize: 20, color: c.muted },
        '&:hover': { bgcolor: c.hover, textDecoration: 'none' },
      }}
    >
      {props.icon}
      <Box component='span' sx={{ flex: 1 }}>
        {props.label}
      </Box>
      <ChevronRight />
    </AppLink>
  )
}

export default function UserMenu(): React.JSX.Element {
  const c = useBrand()
  const { userID, userName, isAdmin } = useSessionInfo()
  const { themeMode, setThemeMode } = useContext(ThemeContext)
  const zone = useTimezones()[userID]
  const place = usePlaces()[userID]
  const dispatch: Dispatch<AnyAction> = useDispatch()
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null)
  const close = (): void => setAnchor(null)

  return (
    <React.Fragment>
      <IconButton
        size='small'
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label='Your account'
        aria-haspopup='dialog'
      >
        <CurrentUserAvatar sx={{ width: 34, height: 34 }} />
      </IconButton>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 300,
              borderRadius: '16px',
              border: `1px solid ${c.line}`,
              bgcolor: c.surface,
              backgroundImage: 'none',
              p: 1,
            },
          },
        }}
        data-cy='manage-profile'
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25 }}>
          <CurrentUserAvatar sx={{ width: 44, height: 44 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, color: c.ink }} noWrap>
              {userName}
            </Typography>
            <Typography sx={{ fontSize: 13, color: c.muted }} noWrap>
              {isAdmin ? 'Admin' : 'Member'}
              {zone ? ` · ${zoneName(zone)}, ${placeCity(zone, place)}` : ''}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ borderTop: `1px solid ${c.line}`, my: 0.5 }} />
        <MenuLink
          to={`/users/${userID}`}
          icon={<Person />}
          label='My profile'
          onClick={close}
        />
        {isAdmin && (
          <MenuLink
            to='/admin'
            icon={<Build />}
            label='Admin panel'
            onClick={close}
          />
        )}

        <Box sx={{ borderTop: `1px solid ${c.line}`, my: 0.5 }} />
        <Typography
          sx={{
            px: 1.25,
            pt: 0.75,
            pb: 0.75,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: c.muted,
          }}
        >
          Appearance
        </Typography>
        <Box
          role='radiogroup'
          aria-label='Appearance'
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 0.5,
            mx: 1,
            p: 0.5,
            borderRadius: '12px',
            bgcolor: c.bg,
            border: `1px solid ${c.line}`,
          }}
        >
          {modes.map((m) => {
            const on = themeMode === m.value
            return (
              <ButtonBase
                key={m.value}
                role='radio'
                aria-checked={on}
                onClick={() => setThemeMode(m.value)}
                sx={{
                  flexDirection: 'column',
                  gap: 0.25,
                  py: 0.75,
                  borderRadius: '9px',
                  fontSize: 12,
                  fontWeight: on ? 600 : 500,
                  color: on ? c.ink : c.muted,
                  bgcolor: on ? c.surface : 'transparent',
                  boxShadow: on ? `0 0 0 1px ${c.line}` : 'none',
                  '& svg': { fontSize: 18 },
                  '&:hover': { color: c.ink },
                }}
              >
                {m.icon}
                {m.label}
              </ButtonBase>
            )
          })}
        </Box>

        <Box sx={{ borderTop: `1px solid ${c.line}`, mt: 1.25, mb: 0.5 }} />
        <ButtonBase
          onClick={() => dispatch(authLogout(true) as AnyAction)}
          sx={{
            width: '100%',
            justifyContent: 'flex-start',
            gap: 1.5,
            px: 1.25,
            height: 40,
            borderRadius: '10px',
            fontSize: 14,
            fontWeight: 500,
            color: c.ink,
            '& svg': { fontSize: 20, color: c.muted },
            '&:hover': { bgcolor: c.hover },
          }}
        >
          <Logout />
          Sign out
        </ButtonBase>
      </Popover>
    </React.Fragment>
  )
}
